import { FC, useState } from 'react'
import { createPortal } from 'react-dom'
import { parsePhoneNumberFromString } from 'libphonenumber-js/min'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { ReactComponent as FacebookIcon } from 'src/assets/svg/facebook.svg'
import { ReactComponent as InstagramIcon } from 'src/assets/svg/instagram.svg'
import { ReactComponent as LinkIcon } from 'src/assets/svg/link.svg'
import { ReactComponent as WhatsappIcon } from 'src/assets/svg/whatsapp.svg'
import { ConversationPanel } from 'src/pages/InboxAnalysisDetail/InboxPreview'
import { useGetInboxConversationMessagesQuery } from 'src/reactQuery/useGetInboxConversationMessagesQuery'
import {
  IntentMessage,
  IntentMessageSentiment,
  useGetTopIntentMessagesQuery,
} from 'src/reactQuery/useGetTopIntentMessagesQuery'
import { ConversationPreview } from 'src/services/__generated__/api'
import { numberWithCommas } from 'src/utils/numberFormat'
import { resolveExternalUrl } from 'src/utils/resolveExternalUrl'
import { IntentRow, SentimentBar, TruncatedLabel } from './parts'
import classes from './topCustomerIntents.module.scss'

interface IntentDetailViewProps {
  fromDate?: string
  intent: IntentRow
  onBack: () => void
  pageId: string
  toDate?: string
}

const SENTIMENT_TABS: { key: IntentMessageSentiment; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'positive', label: 'Positive' },
  { key: 'neutral', label: 'Neutral' },
  { key: 'negative', label: 'Negative' },
]

const OperatorIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    height="14"
    viewBox="0 0 14 14"
    width="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 7.5a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Zm0 1.5c-2.21 0-5 1.11-5 3.33V13h10v-.67C12 10.11 9.21 9 7 9Z"
      fill="currentColor"
    />
  </svg>
)

const platformIcon = (platform: string | null) => {
  switch ((platform || '').toLowerCase()) {
    case 'facebook':
      return <FacebookIcon />
    case 'instagram':
      return <InstagramIcon />
    case 'whatsapp':
      return <WhatsappIcon />
    default:
      return null
  }
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

const isoToFlagEmoji = (iso: string) =>
  iso
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('')

const phoneToFlag = (name: string): string | null => {
  const trimmed = name.trim()
  const candidate = trimmed.startsWith('+') ? trimmed : `+${trimmed}`
  const parsed = parsePhoneNumberFromString(candidate)
  return parsed?.country ? isoToFlagEmoji(parsed.country) : null
}

const avatarContent = (platform: string | null, name: string) => {
  if ((platform || '').toLowerCase() === 'whatsapp') {
    const flag = phoneToFlag(name)
    if (flag) return flag
  }
  return getInitials(name)
}

const formatRelative = (iso: string | null) => {
  if (!iso) return ''
  const diffMs = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(diffMs) || diffMs < 0) return ''
  const min = Math.floor(diffMs / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d ago`
  const mo = Math.floor(d / 30)
  if (mo < 12) return `${mo}mo ago`
  return `${Math.floor(mo / 12)}y ago`
}

const toConversationPreview = (m: IntentMessage): ConversationPreview => ({
  externalUrl: m.externalUrl,
  id: m.conversationId,
  lastActivityAt: m.createdAt,
  operator: m.operator ?? { id: null, name: 'Unassigned' },
  platform: m.platform,
  sentiment: m.sentiment,
  snippet: m.snippet,
  topics: [],
  user: m.user,
})

export const IntentDetailView: FC<IntentDetailViewProps> = ({
  fromDate,
  intent,
  onBack,
  pageId,
  toDate,
}) => {
  const [sentiment, setSentiment] = useState<IntentMessageSentiment>('all')
  const [selected, setSelected] = useState<ConversationPreview | null>(null)

  // The conversation modal is portalled to <body>; without this the page
  // behind it keeps scrolling while the modal is open.
  useDisableScroll(Boolean(selected))

  const { data: messagesData, isLoading: messagesLoading } =
    useGetInboxConversationMessagesQuery({
      conversationId: selected?.id ?? null,
      limit: 200,
    })

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useGetTopIntentMessagesQuery({
      fromDate,
      intent: intent.id,
      limit: 20,
      pageId,
      sentiment,
      toDate,
    })

  const messages = data?.pages.flatMap((p) => p.messages) ?? []

  return (
    <>
      {/* ── Back link (no border) ── */}
      <button className={classes.backButton} onClick={onBack} type="button">
        ← All Intents
      </button>

      {/* ── Selected intent summary card ── */}
      <div className={classes.detailCard}>
        <div className={classes.detailCardLeft}>
          <div className={classes.detailTitleRow}>
            <h3 className={classes.detailName}>{intent.name}</h3>
          </div>
          <span className={classes.detailTotal}>
            {numberWithCommas(intent.total)} messages total
          </span>
        </div>
        <div className={classes.detailCardBar}>
          <SentimentBar row={intent} />
        </div>
      </div>

      {/* ── Sentiment switcher (segmented control, matches Insights Summary) ── */}
      <div className={classes.detailSwitcher}>
        {SENTIMENT_TABS.map((t) => (
          <button
            className={`${classes.filterTab} ${
              sentiment === t.key ? classes.filterTabActive : ''
            }`}
            key={t.key}
            onClick={() => setSentiment(t.key)}
            type="button"
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Scrollable message list ── */}
      <div className={classes.messagesCard}>
        <div className={classes.messagesScroll}>
          {messages.length === 0 && !isFetching && (
            <div className={classes.messagesEmpty}>No messages</div>
          )}

          {messages.map((m) => {
            const href = resolveExternalUrl(m.externalUrl)
            const operatorName = m.operator?.name || 'Unassigned'
            return (
              <div
                className={classes.messageRow}
                key={m.id}
                onClick={() => setSelected(toConversationPreview(m))}
                role="button"
                tabIndex={0}
              >
                <div className={classes.messageAvatar}>
                  {avatarContent(m.platform, m.user.name)}
                </div>

                <div className={classes.messageMain}>
                  <div className={classes.messageHeader}>
                    {platformIcon(m.platform)}
                    <span className={classes.messageUserName}>
                      {m.user.name}
                    </span>
                    <span className={classes.messageTimeAgo}>
                      · {formatRelative(m.createdAt)}
                    </span>
                    {href && (
                      <a
                        className={classes.messageLinkIcon}
                        href={href}
                        onClick={(e) => e.stopPropagation()}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <LinkIcon />
                      </a>
                    )}
                  </div>
                  <TruncatedLabel
                    className={classes.messageSnippet}
                    text={m.snippet}
                  />
                </div>

                <div className={classes.messageRight}>
                  <span className={`${classes.pill} ${classes[m.sentiment]}`}>
                    {m.sentiment}
                  </span>
                  <span className={classes.messageOperator}>
                    <OperatorIcon className={classes.messageOperatorIcon} />
                    Operator: <strong>{operatorName}</strong>
                  </span>
                  <span className={classes.messageCount}>
                    {numberWithCommas(m.messageCount ?? 0)} message
                    {m.messageCount === 1 ? '' : 's'}
                  </span>
                </div>
              </div>
            )
          })}

          {isFetching && !messages.length && (
            <div className={classes.messagesEmpty}>Loading…</div>
          )}

          {hasNextPage && (
            <button
              className={classes.loadMore}
              disabled={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              type="button"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>

      {selected &&
        createPortal(
          <div
            className={classes.conversationModalBackdrop}
            onClick={() => setSelected(null)}
          >
            <div
              className={classes.conversationModal}
              onClick={(e) => e.stopPropagation()}
            >
              <ConversationPanel
                conversation={selected}
                isLoading={messagesLoading}
                messages={messagesData?.messages ?? []}
                onClose={() => setSelected(null)}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
